import { FolderIconClosed } from '@/utils/icons';
import { RenameInput } from '../../RenameInput/RenameInput';
import { PendingSpinner } from '../../PendingSpinner/PendingSpinner';
import type { IFolderItemProps } from '../itemProps';
import { useFolderDnd } from '../hooks/useFolderDnd';
import styles from './DocumentTable.module.css';

// A subfolder of the current folder, listed above the files. Clicking it
// navigates into the folder (table + sidebar both follow the selection).
// Folders carry no size/status/version metadata, so those cells stay empty.
export function FolderRow({
  folder, isRejected, isMenuTarget = false, rename, onOpen, onContextMenu,
}: IFolderItemProps) {
  const isRenaming = Boolean(rename);
  // Its id is about to change (renamed or moved, or under a folder that is).
  const isSaving = Boolean(folder.saving);
  const locked = isRenaming || isSaving;

  // Same rules as the sidebar: system roots can receive drops but are fixed
  // where the server put them, so they can't be picked up.
  const { setRef, attributes, listeners, style, dropHighlight } =
    useFolderDnd(folder.id, Boolean(folder.isSystem) || locked, isSaving);

  return (
    <tr
      ref={setRef}
      style={style}
      className={`${styles.row} ${isMenuTarget ? styles.rowSelected : ''} ${dropHighlight ? styles.dropTarget : ''} ${isRejected ? styles.dragRejected : ''} ${isSaving ? styles.rowSaving : ''}`}
      aria-busy={isSaving || undefined}
      onClick={() => !locked && onOpen(folder.id)}
      onContextMenu={(e) => (isSaving ? e.preventDefault() : onContextMenu?.(e, folder.id))}
      onKeyDown={(e) => {
        if (!locked && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onOpen(folder.id);
        }
      }}
      {...attributes}
      {...listeners}
    >
      <td className={`${styles.td} ${styles.colName}`}>
        <span className={`${styles.nameCell} ${styles.folderNameCell}`}>
          <span className={styles.folderRowIcon}><FolderIconClosed size={16} /></span>
          <span>
            {rename ? (
              <RenameInput {...rename} />
            ) : (
              <span className={styles.docName}>{folder.name}{isSaving && <PendingSpinner />}</span>
            )}
          </span>
        </span>
      </td>
      <td className={`${styles.td} ${styles.colDesc}`} />
      <td className={`${styles.td} ${styles.colVersion}`} />
      <td className={`${styles.td} ${styles.colStatus}`} />
      <td className={`${styles.td} ${styles.colSize}`} />
      <td className={`${styles.td} ${styles.colUpdated}`} />
    </tr>
  );
}
